import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { updateUser, checkAuth } = useAuth();

  useEffect(() => {
    // Prevent double-processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        toast.error('Authentication failed');
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Exchange session_id for session data
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const { user, token } = response.data;

        // Store token
        localStorage.setItem('aura_token', token);

        // Check if we have a pending role from registration
        const pendingRole = localStorage.getItem('aura_pending_role');
        if (pendingRole && user.role !== pendingRole) {
          // Update user role if they selected one during registration
          try {
            const updateResponse = await axios.put(
              `${API}/auth/profile`,
              { role: pendingRole },
              { 
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true 
              }
            );
            user.role = pendingRole;
          } catch (e) {
            console.error('Failed to update role:', e);
          }
          localStorage.removeItem('aura_pending_role');
        }

        // Update auth context
        updateUser(user);

        toast.success('Welcome to Aura!');

        // Check if athlete needs onboarding (new user or hasn't completed)
        if (user.role === 'athlete' && !user.onboarding_completed) {
          navigate('/onboarding', { replace: true });
          return;
        }

        // Navigate based on role
        const targetPath = user.role === 'supporter' ? '/supporter' : '/dashboard';
        navigate(targetPath, { replace: true, state: { user } });

      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-aura-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-aura-text-muted font-heading uppercase tracking-wider">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
